import React from 'react'
import {Link as ReactRouterLink, useHistory, useParams} from 'react-router-dom'
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '../../../../utils/chakraMonomorphic'
import {RouterLink as Link} from '../../../../utils/chakraRouterLink'

import {useConfirmation} from '../../../../app/ConfirmationManager'
import {useRequiredAuth} from '../../../../stores/AuthStore'
import {
  useFaceOffGoldenVersion,
  useFaceOffGoldenVersions,
} from '../../../../hooks/useFaceOffGolden'
import {api} from '../../../../services/api'
import {FACE_OFF_CONFLICT} from '../../../../services/api/faceoff'
import {FaceOffGoldenEdit} from '../../../../services/coreApi/faceoff'
import copy from './copy.json'
import {FaceOffGoldenReview, caseId} from './FaceOffGoldenReview'
import {
  GoldenDetails,
  QueueCaseButton,
  QueuePhotoButton,
  RemovalQueueBar,
  afterRemovals,
  useRemovalQueue,
} from './FaceOffGoldenQueue'
import {formatScore} from './faceOffFaces'
import {
  FACE_OFF_GOLDEN_PATH,
  FACE_OFF_PATH,
  MAX_GOLDEN_CASES,
  MAX_GOLDEN_PHOTOS,
  faceOffGoldenVersionPath,
  fillCopy,
} from './constants'

const Loading: React.FC<{text: string; testId: string}> = ({text, testId}) => (
  <Flex alignItems='center' gap={3} data-testid={testId}>
    <Spinner size='sm' />
    <Text fontSize='sm'>{text}</Text>
  </Flex>
)

const BackLink: React.FC<{to: string; text: string}> = ({to, text}) => (
  <Box mb={4}>
    <Link
      as={ReactRouterLink}
      to={to}
      color='var(--fo-link)'
      textDecoration='underline'>
      {text}
    </Link>
  </Box>
)

/** Every golden version, newest first. */
export const FaceOffGoldenList: React.FC = () => {
  const {status, data} = useFaceOffGoldenVersions()

  const body = ((): React.ReactElement => {
    if (status === 'pending' || status === 'idle') {
      return (
        <Loading text={copy.golden.loading} testId='face-off-golden-loading' />
      )
    }
    if (status === 'rejected' || !data) {
      return (
        <Text
          color='var(--fo-red)'
          fontSize='sm'
          data-testid='face-off-golden-error'>
          {copy.golden.error}
        </Text>
      )
    }
    if (data.length === 0) {
      return <Text fontSize='sm'>{copy.golden.empty}</Text>
    }
    return (
      <Table size='sm' variant='simple'>
        <Thead>
          <Tr>
            <Th>{copy.golden.columns.version}</Th>
            <Th>{copy.golden.columns.created}</Th>
            <Th isNumeric>{copy.golden.columns.photos}</Th>
            <Th isNumeric>{copy.golden.columns.cases}</Th>
            <Th isNumeric>{copy.golden.columns.extends}</Th>
            <Th isNumeric>{copy.golden.columns.lastPassRate}</Th>
            <Th>{copy.golden.columns.source}</Th>
            <Th>{copy.golden.columns.note}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((summary) => (
            <Tr key={summary.version} data-testid='face-off-golden-version'>
              <Td>
                <Link
                  as={ReactRouterLink}
                  to={faceOffGoldenVersionPath(summary.version)}
                  color='var(--fo-link)'
                  textDecoration='underline'
                  data-testid='face-off-golden-version-link'>
                  {fillCopy(copy.golden.versionLabel, {
                    version: summary.version,
                  })}
                </Link>
              </Td>
              <Td>{new Date(summary.createdUtc).toLocaleString()}</Td>
              <Td isNumeric>{summary.photos}</Td>
              <Td isNumeric>{summary.cases}</Td>
              <Td isNumeric>
                {summary.extends === null
                  ? ''
                  : fillCopy(copy.golden.versionLabel, {
                      version: summary.extends,
                    })}
              </Td>
              <Td isNumeric data-testid='face-off-golden-last-pass'>
                {summary.lastPassRate === null
                  ? ''
                  : `${(summary.lastPassRate * 100).toFixed(1)}%`}
              </Td>
              <Td>{summary.source ?? ''}</Td>
              <Td>{summary.note ?? ''}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    )
  })()

  return (
    <Box data-testid='face-off-golden'>
      <BackLink to={FACE_OFF_PATH} text={copy.backToRuns} />
      <Heading size='md' mb={4}>
        {copy.golden.heading}
      </Heading>
      {body}
    </Box>
  )
}

/**
 * One golden version's details, photos and cases, with the caps. Only the
 * newest version offers removals, because media extends the newest version.
 * Removals queue up and Apply writes them all as one new version.
 */
export const FaceOffGoldenVersionView: React.FC = () => {
  const params = useParams<{version: string}>()
  const version = Number(params.version)
  const history = useHistory()
  const confirm = useConfirmation()
  const {status, data, notFound} = useFaceOffGoldenVersion(version)
  // Reload the list per version, so the newest is known after an edit.
  const versions = useFaceOffGoldenVersions(String(version))
  const [editError, setEditError] = React.useState<string | null>(null)
  const [jumpTo, setJumpTo] = React.useState<string | null>(null)
  // The review opens over the whole screen, as Face Off's does; Esc or All
  // cases closes it to the list below.
  const [reviewing, setReviewing] = React.useState(true)
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const {client} = useRequiredAuth()
  const queue = useRemovalQueue(version)

  React.useEffect(() => {
    setEditError(null)
  }, [version])

  const newest =
    versions.data && versions.data.length > 0
      ? Math.max(...versions.data.map((v) => v.version))
      : null
  const canEdit = newest !== null && newest === version

  const runEdit = async (edit: FaceOffGoldenEdit): Promise<void> => {
    setEditError(null)
    const response = await api.faceoff.editFaceOffGolden(edit)
    if (response.result === 'success') {
      history.push(faceOffGoldenVersionPath(response.data.version))
    } else {
      setEditError(
        response.error.message === FACE_OFF_CONFLICT
          ? copy.golden.conflict
          : copy.golden.editError,
      )
    }
  }

  const askToApply = (): void => {
    if (!data || queue.removals.length === 0) return
    const {removals} = queue
    const left = afterRemovals(data.photos, removals)
    confirm({
      variant: 'danger',
      title: copy.golden.confirmTitle,
      description: fillCopy(copy.golden.confirmApply, {
        count: removals.length,
        gone: left.gone,
        left: left.cases,
      }),
      confirmButtonText: copy.golden.confirmButton,
      testID: 'face-off-golden-confirm',
      onConfirm: () => runEdit({action: 'remove-many', removals}),
    })
  }

  if (status === 'pending' || status === 'idle') {
    return (
      <Loading
        text={copy.golden.versionLoading}
        testId='face-off-golden-version-loading'
      />
    )
  }

  if (status === 'rejected' || !data) {
    return (
      <>
        <BackLink to={FACE_OFF_GOLDEN_PATH} text={copy.golden.backToVersions} />
        <Text
          color={notFound ? 'var(--fo-muted)' : 'var(--fo-red)'}
          fontSize='sm'
          data-testid={
            notFound
              ? 'face-off-golden-version-not-found'
              : 'face-off-golden-version-error'
          }>
          {notFound ? copy.golden.versionNotFound : copy.golden.versionError}
        </Text>
      </>
    )
  }

  const caseCount = data.photos.reduce((sum, p) => sum + p.cases.length, 0)
  // An older media sends no summary; the version list still has one.
  const summary =
    data.summary ??
    versions.data?.find((v) => v.version === data.version) ??
    null
  const queueBar = canEdit ? (
    <RemovalQueueBar queue={queue} photos={data.photos} onApply={askToApply} />
  ) : null

  return (
    <Box data-testid='face-off-golden-version-detail'>
      <BackLink to={FACE_OFF_GOLDEN_PATH} text={copy.golden.backToVersions} />
      <Heading size='md' mb={2}>
        {fillCopy(copy.golden.versionHeading, {version: data.version})}
      </Heading>
      <Text fontSize='sm' data-testid='face-off-golden-photo-cap'>
        {fillCopy(copy.golden.photoCap, {
          photos: data.photos.length,
          max: MAX_GOLDEN_PHOTOS,
        })}
      </Text>
      <Text fontSize='sm' mb={4} data-testid='face-off-golden-case-cap'>
        {fillCopy(copy.golden.caseCap, {
          cases: caseCount,
          max: MAX_GOLDEN_CASES,
        })}
      </Text>
      {summary && (
        <Box mb={4}>
          <GoldenDetails summary={summary} />
        </Box>
      )}
      {editError && (
        <Text
          color='var(--fo-red)'
          fontSize='sm'
          mb={4}
          data-testid='face-off-golden-edit-error'>
          {editError}
        </Text>
      )}
      <FaceOffGoldenReview
        data={data}
        clientId={Number(client.ID)}
        jumpTo={jumpTo}
        summary={summary}
        queue={canEdit ? queue : null}
        queueBar={queueBar}
        open={reviewing}
        onOpen={(): void => setReviewing(true)}
        onAllCases={(): void => {
          setReviewing(false)
          setJumpTo(null)
          listRef.current?.scrollIntoView?.({block: 'start'})
        }}
      />
      <Heading size='sm' mb={3} ref={listRef}>
        {copy.review.allCases}
      </Heading>
      {queueBar && <Box mb={4}>{queueBar}</Box>}
      {data.photos.map((photo) => (
        <Box
          key={photo.photoId}
          borderWidth='1px'
          borderRadius='md'
          p={4}
          mb={4}
          data-testid='face-off-golden-photo'>
          <Flex gap={4} alignItems='flex-start'>
            <Image
              src={photo.url}
              alt={String(photo.photoId)}
              maxH='200px'
              maxW='40%'
              objectFit='contain'
            />
            <Box flex='1'>
              <Text fontSize='sm' mb={2}>
                {fillCopy(copy.golden.photoMeta, {
                  clientId: photo.clientId,
                  albumId: photo.albumId,
                  reason: photo.reason,
                })}
              </Text>
              {photo.cases.map((c) => (
                <Flex
                  key={c.personId}
                  gap={3}
                  alignItems='center'
                  mb={1}
                  data-testid='face-off-golden-case'>
                  <Text fontSize='sm'>
                    {fillCopy(copy.golden.caseLine, {
                      personId: c.personId,
                      truth: c.truth,
                      kind: c.kind,
                      baselineScore:
                        c.baselineScore === null
                          ? copy.diff.noScore
                          : formatScore(c.baselineScore),
                      score: formatScore(c.score),
                      refs: c.refs,
                    })}
                  </Text>
                  <Button
                    size='xs'
                    variant='ghost'
                    onClick={(): void => {
                      setJumpTo(caseId(photo.photoId, c.personId))
                      setReviewing(true)
                    }}
                    data-testid='face-off-golden-case-open'>
                    {copy.golden.openCase}
                  </Button>
                  {canEdit && (
                    <QueueCaseButton
                      queue={queue}
                      photoId={photo.photoId}
                      personId={c.personId}
                    />
                  )}
                </Flex>
              ))}
              {canEdit && (
                <Box mt={2}>
                  <QueuePhotoButton queue={queue} photo={photo} />
                </Box>
              )}
            </Box>
          </Flex>
        </Box>
      ))}
    </Box>
  )
}
